/**
 * Matches a value from one enum to another enum based on the key.
 *
 * @template TargetEnum - The first enum type.
 * @template SourceEnum - The second enum type.
 * @param {TargetEnum} targetEnum - The first enum to match the value to.
 * @param {SourceEnum} sourceEnum - The second enum containing the value to match.
 * @param {SourceEnum[keyof SourceEnum]} value - The value from the second enum to match.
 * @returns {TargetEnum[keyof TargetEnum] | undefined} - The matching value from the first enum, or undefined if no match is found.
 */
export default function getMatchingEnumValue<
  SourceEnum extends Record<string, string | number>,
  TargetEnum extends Record<string, string | number>,
>(
  sourceEnum: SourceEnum,
  targetEnum: TargetEnum,
  value: SourceEnum[keyof SourceEnum],
): TargetEnum[keyof TargetEnum] | undefined {
  const enumKey = (Object.keys(sourceEnum) as Array<keyof SourceEnum>).find(
    (key) => sourceEnum[key] === value,
  );

  return enumKey ? targetEnum[enumKey as unknown as keyof TargetEnum] : undefined;
}
